import React, { useEffect, useRef, useState } from "react";
import { api } from "./api";

/** Step 1: request a sign-in link. The answer is always the same sentence. */
export function SignIn({ notice }: { notice?: string }): React.ReactElement {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await api<{ message: string }>("POST", "/api/auth/link", { email });
    setBusy(false);
    if (r.ok) setSent(r.data.message);
    else setError(r.data.error ?? "Something went wrong. Try again.");
  }

  return (
    <div className="section-card">
      <h2 className="text-lg font-semibold mb-1">Sign in</h2>
      {notice && <p className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">{notice}</p>}
      {sent ? (
        <div role="status">
          <p className="text-sm text-slate-700">{sent}</p>
          <p className="field-hint mt-3">Open the link on this device. You'll then enter your 4-digit PIN.</p>
          <button type="button" className="btn-secondary mt-4" onClick={() => setSent(null)}>
            Use a different email
          </button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <p className="text-sm text-slate-600 mb-4">We'll email you a sign-in link. It works once and expires in 15 minutes.</p>
          <label htmlFor="email" className="field-label">
            Email
          </label>
          <input
            id="email"
            className="field-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {error && <p className="field-error">{error}</p>}
          <button type="submit" className="btn-primary mt-4 w-full sm:w-auto" disabled={busy || !email.trim()}>
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>
        </form>
      )}
    </div>
  );
}

/** Step 2: the PIN. One numeric field, 4 digits (Round 25 lesson: numeric keypad, no punctuation needed). */
export function PinEntry({ token, onSignedIn }: { token: string; onSignedIn: () => void }): React.ReactElement {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    if (pin.length !== 4) return;
    setBusy(true);
    setError(null);
    const r = await api("POST", "/api/auth/pin", { t: token, pin });
    setBusy(false);
    if (r.ok) {
      // Drop the token from the address bar and history.
      window.history.replaceState(null, "", "/");
      onSignedIn();
      return;
    }
    setPin("");
    const msg = r.data.error ?? "Something went wrong. Try again.";
    if (r.status === 423 || r.status === 410 || (r.status === 400 && r.data.code !== "malformed")) setDead(msg);
    else setError(msg);
    input.current?.focus();
  }

  if (!token) {
    return (
      <div className="section-card">
        <h2 className="text-lg font-semibold mb-2">This link is incomplete</h2>
        <p className="text-sm text-slate-700 mb-4">Open the link from your email again, or request a new one.</p>
        <a className="btn-primary" href="/">
          Request a new link
        </a>
      </div>
    );
  }

  if (dead) {
    return (
      <div className="section-card" role="alert">
        <h2 className="text-lg font-semibold mb-2">Can't sign in with this link</h2>
        <p className="text-sm text-slate-700 mb-4">{dead}</p>
        <a className="btn-primary" href="/">
          Request a new link
        </a>
      </div>
    );
  }

  return (
    <form className="section-card" onSubmit={submit} noValidate>
      <h2 className="text-lg font-semibold mb-1">Enter your PIN</h2>
      <p className="text-sm text-slate-600 mb-4">Your 4-digit PIN was given to you by a System Admin. It is never emailed.</p>
      <label htmlFor="pin" className="field-label">
        PIN
      </label>
      <input
        id="pin"
        ref={input}
        className="field-input text-center text-2xl tracking-[0.5em] font-mono max-w-[10rem]"
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={4}
        value={pin}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
          setPin(digits);
          setError(null);
        }}
        aria-describedby={error ? "pin-error" : undefined}
      />
      {error && (
        <p id="pin-error" className="field-error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn-primary mt-4 w-full sm:w-auto" disabled={busy || pin.length !== 4}>
        {busy ? "Checking…" : "Sign in"}
      </button>
    </form>
  );
}
